import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { useOrgRole } from "@/hooks/useOrgRole";
import { Switch } from "@/components/ui/switch";
import { RoleGate } from "@/components/RoleGate";
import NotFound from "@/pages/NotFound";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Download, AlertTriangle, Copy, Search, Snowflake, Frown, Hash, History, MessageSquare, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";

type SortKey = "updated_at" | "name" | "observation_count" | "formality" | "verbosity" | "sentiment_baseline";
type HistoryWindow = 24 | 48 | 72;

interface RecentMessage {
  id: string;
  content: string;
  message_type: string;
  sender_name: string;
  sender_phone: string;
  remote_jid: string;
  created_at: string;
  whatsapp_timestamp: string | null;
}

interface TomProfile {
  id: string;
  member_jid: string;
  display_name: string | null;
  formality: number | null;
  verbosity: number | null;
  sentiment_baseline: number | null;
  topics_of_interest: string[] | null;
  last_intent: string | null;
  observation_count: number | null;
  created_at: string;
  updated_at: string;
}

interface TomCalibrationRow {
  id: string;
  sample_size: number | null;
  brier_score: number | null;
  ece: number | null;
  metric_window_start: string;
  metric_window_end: string;
  created_at: string;
}

interface TomContributionRow {
  id: string;
  contribution_score: number | null;
  winner: string | null;
  with_tom_score: number | null;
  without_tom_score: number | null;
  created_at: string;
}

interface SyraGroupRow {
  id: string;
  name: string;
  member_count: number | null;
  active: boolean;
}

const STALE_DAYS = 30;

function MiniBar({ value, min = 0, max = 1, tone = "primary" }: { value: number; min?: number; max?: number; tone?: "primary" | "sentiment" }) {
  const range = max - min;
  const pct = Math.max(0, Math.min(1, (value - min) / range));
  if (tone === "sentiment") {
    const center = 0.5;
    const fillPct = Math.abs(pct - center) * 2;
    const left = pct < center ? `${pct * 100}%` : `${center * 100}%`;
    const width = `${fillPct * 100}%`;
    const color = pct < center ? "bg-destructive" : pct > center ? "bg-success" : "bg-muted-foreground";
    return (
      <div className="relative h-1.5 w-20 rounded-full bg-muted overflow-hidden">
        <div className="absolute top-0 bottom-0 w-px bg-foreground/30" style={{ left: "50%" }} />
        <div className={`absolute top-0 bottom-0 ${color}`} style={{ left, width }} />
      </div>
    );
  }
  return (
    <div className="relative h-1.5 w-20 rounded-full bg-muted overflow-hidden">
      <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${pct * 100}%` }} />
    </div>
  );
}

function relTime(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return iso;
  }
}

function escapeCsv(s: string): string {
  if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function CortexTomExplorerInner() {
  const { data: defaultOrgId } = useCurrentOrgId();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const orgId = selectedOrgId ?? defaultOrgId ?? null;
  const { role } = useOrgRole();
  const canManage = role === "owner" || role === "admin" || role === "superadmin";
  const qc = useQueryClient();
  const [flagBusy, setFlagBusy] = useState(false);

  async function toggleTomFlag(nextEnabled: boolean) {
    if (!orgId || !canManage) return;
    setFlagBusy(true);
    try {
      const { error } = await supabase
        .from("feature_flags")
        .upsert(
          {
            org_id: orgId,
            flag_key: "cortex.tom",
            enabled: nextEnabled,
            rollout_pct: nextEnabled ? 100 : 0,
            description: "cortex.tom",
          },
          { onConflict: "org_id,flag_key" },
        );
      if (error) throw new Error(error.message);
      toast.success(`cortex.tom ${nextEnabled ? "ativada" : "desativada"}`);
      qc.invalidateQueries({ queryKey: ["cortex-tom", "flag", orgId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao alternar flag");
    } finally {
      setFlagBusy(false);
    }
  }

  const { data: availableOrgs } = useQuery({
    queryKey: ["cortex-tom", "user-orgs"],
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("org_members")
        .select("org_id, organizations:org_id(id, name)")
        .eq("user_id", user.id);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Array<{ organizations: { id: string; name: string } | null }>;
      return rows
        .map((r) => r.organizations)
        .filter((o): o is { id: string; name: string } => !!o)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("updated_at");
  const [filterStale, setFilterStale] = useState(false);
  const [filterNegative, setFilterNegative] = useState(false);
  const [minObs, setMinObs] = useState<number>(0);
  const [activeProfile, setActiveProfile] = useState<TomProfile | null>(null);
  const [topicFilter, setTopicFilter] = useState<string | null>(null);
  const [historyWindow, setHistoryWindow] = useState<HistoryWindow | null>(null);
  const [historySearch, setHistorySearch] = useState("");
  const [historyMemberFilter, setHistoryMemberFilter] = useState<string | null>(null);

  const { data: recentMessages, isLoading: loadingHistory, refetch: refetchHistory } = useQuery({
    queryKey: ["cortex-tom", "recent-messages", orgId, selectedGroupId, historyWindow],
    enabled: !!orgId && !!selectedGroupId && !!historyWindow,
    queryFn: async (): Promise<RecentMessage[]> => {
      const since = new Date(Date.now() - historyWindow! * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("syra_messages")
        .select("id, content, message_type, sender_name, sender_phone, remote_jid, created_at, whatsapp_timestamp")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw new Error(error.message);
      return (data ?? []) as RecentMessage[];
    },
  });

  const historyStats = useMemo(() => {
    if (!recentMessages?.length) return null;
    const byMember = new Map<string, { name: string; phone: string; count: number }>();
    for (const m of recentMessages) {
      const key = m.sender_phone || m.remote_jid;
      const cur = byMember.get(key);
      if (cur) cur.count++;
      else byMember.set(key, { name: m.sender_name || key, phone: key, count: 1 });
    }
    const top = Array.from(byMember.values()).sort((a, b) => b.count - a.count).slice(0, 12);
    return { total: recentMessages.length, distinctSenders: byMember.size, top };
  }, [recentMessages]);

  const filteredMessages = useMemo(() => {
    if (!recentMessages) return [];
    const q = historySearch.trim().toLowerCase();
    return recentMessages.filter((m) => {
      if (historyMemberFilter && (m.sender_phone || m.remote_jid) !== historyMemberFilter) return false;
      if (q) {
        const hay = `${m.sender_name} ${m.content}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [recentMessages, historySearch, historyMemberFilter]);

  function exportHistoryCsv() {
    if (!filteredMessages.length || !selectedGroup) return;
    const headers = ["created_at", "sender_name", "sender_phone", "message_type", "content"];
    const lines = [headers.join(",")];
    for (const m of filteredMessages) {
      lines.push([
        m.created_at,
        escapeCsv(m.sender_name ?? ""),
        escapeCsv(m.sender_phone ?? ""),
        escapeCsv(m.message_type ?? ""),
        escapeCsv(m.content ?? ""),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = selectedGroup.name.replace(/\W+/g, "_");
    a.href = url;
    a.download = `syra-historico-${safeName}-${historyWindow}h.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Histórico exportado (${filteredMessages.length} mensagens)`);
  }

  const { data: groups } = useQuery({
    queryKey: ["cortex-tom", "groups", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<SyraGroupRow[]> => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name, member_count, active")
        .eq("org_id", orgId!)
        .eq("active", true)
        .order("name");
      if (error) throw new Error(error.message);
      return (data ?? []) as SyraGroupRow[];
    },
  });

  const { data: profiles, isLoading: loadingProfiles } = useQuery({
    queryKey: ["cortex-tom", "profiles", orgId, selectedGroupId],
    enabled: !!orgId && !!selectedGroupId,
    refetchInterval: 60_000,
    queryFn: async (): Promise<TomProfile[]> => {
      const { data, error } = await supabase
        .from("tom_models")
        .select("id, member_jid, display_name, formality, verbosity, sentiment_baseline, topics_of_interest, last_intent, observation_count, created_at, updated_at")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .order("updated_at", { ascending: false })
        .limit(500);
      if (error) throw new Error(error.message);
      return (data ?? []) as TomProfile[];
    },
  });

  const { data: calibrationRuns } = useQuery({
    queryKey: ["cortex-tom", "calibration", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<TomCalibrationRow[]> => {
      const { data, error } = await supabase
        .from("tom_calibration")
        .select("id, sample_size, brier_score, ece, metric_window_start, metric_window_end, created_at")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw new Error(error.message);
      return (data ?? []) as TomCalibrationRow[];
    },
  });

  const { data: contributionRuns } = useQuery({
    queryKey: ["cortex-tom", "contribution", orgId, selectedGroupId],
    enabled: !!orgId,
    queryFn: async (): Promise<TomContributionRow[]> => {
      let query = supabase
        .from("tom_contribution_runs")
        .select("id, contribution_score, winner, with_tom_score, without_tom_score, created_at")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(20);
      if (selectedGroupId) query = query.eq("group_id", selectedGroupId);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []) as TomContributionRow[];
    },
  });

  const { data: tomFlag } = useQuery({
    queryKey: ["cortex-tom", "flag", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("enabled, rollout_pct")
        .eq("org_id", orgId!)
        .eq("flag_key", "cortex.tom")
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data ?? { enabled: false, rollout_pct: 0 };
    },
  });

  const stats = useMemo(() => {
    if (!profiles?.length) return null;
    const n = profiles.length;
    const mean = (f: (p: TomProfile) => number) => profiles.reduce((a, b) => a + (f(b) || 0), 0) / n;
    const staleCutoff = Date.now() - STALE_DAYS * 24 * 3600 * 1000;
    const stale = profiles.filter((p) => new Date(p.updated_at).getTime() < staleCutoff).length;
    return {
      total: n,
      avgFormality: mean((p) => Number(p.formality ?? 0)),
      avgVerbosity: mean((p) => Number(p.verbosity ?? 0)),
      avgSentiment: mean((p) => Number(p.sentiment_baseline ?? 0)),
      totalObservations: profiles.reduce((a, b) => a + (b.observation_count ?? 0), 0),
      staleCount: stale,
    };
  }, [profiles]);

  const filtered = useMemo(() => {
    if (!profiles) return [];
    const q = search.trim().toLowerCase();
    const staleCutoff = Date.now() - STALE_DAYS * 24 * 3600 * 1000;
    let rows = profiles.filter((p) => {
      if (q) {
        const hay = `${p.display_name ?? ""} ${p.member_jid}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterStale && new Date(p.updated_at).getTime() >= staleCutoff) return false;
      if (filterNegative && Number(p.sentiment_baseline ?? 0) >= 0) return false;
      if (minObs > 0 && (p.observation_count ?? 0) < minObs) return false;
      if (topicFilter && !(p.topics_of_interest ?? []).includes(topicFilter)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      switch (sortKey) {
        case "name":
          return (a.display_name ?? a.member_jid).localeCompare(b.display_name ?? b.member_jid);
        case "observation_count":
          return (b.observation_count ?? 0) - (a.observation_count ?? 0);
        case "formality":
          return Number(b.formality ?? 0) - Number(a.formality ?? 0);
        case "verbosity":
          return Number(b.verbosity ?? 0) - Number(a.verbosity ?? 0);
        case "sentiment_baseline":
          return Number(b.sentiment_baseline ?? 0) - Number(a.sentiment_baseline ?? 0);
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
    });
    return rows;
  }, [profiles, search, filterStale, filterNegative, minObs, topicFilter, sortKey]);

  const selectedGroup = groups?.find((g) => g.id === selectedGroupId);
  const flagOff = tomFlag && !tomFlag.enabled;
  const partialRollout = tomFlag?.enabled && (tomFlag.rollout_pct ?? 100) < 100;
  const latestCalibration = calibrationRuns?.[0] ?? null;
  const contributionAvg = contributionRuns?.length
    ? contributionRuns.reduce((sum, row) => sum + Number(row.contribution_score ?? 0), 0) / contributionRuns.length
    : null;
  const withTomWins = contributionRuns?.filter((row) => row.winner === "with_tom").length ?? 0;

  function exportCsv() {
    if (!filtered.length || !selectedGroup) return;
    const headers = [
      "member_jid", "display_name", "formality", "verbosity", "sentiment_baseline",
      "topics_of_interest", "last_intent", "observation_count", "created_at", "updated_at",
    ];
    const lines = [headers.join(",")];
    for (const p of filtered) {
      lines.push([
        escapeCsv(p.member_jid),
        escapeCsv(p.display_name ?? ""),
        String(p.formality ?? ""),
        String(p.verbosity ?? ""),
        String(p.sentiment_baseline ?? ""),
        escapeCsv((p.topics_of_interest ?? []).join("|")),
        escapeCsv(p.last_intent ?? ""),
        String(p.observation_count ?? 0),
        p.created_at,
        p.updated_at,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = selectedGroup.name.replace(/\W+/g, "_");
    const isoDate = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `syra-tom-${safeName}-${isoDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV exportado (${filtered.length} perfis)`);
  }

  return (
    <div className="space-y-4">
      <header>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="text-xl font-bold font-headline tracking-tight">Theory of Mind</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Perfil comportamental por participante, estimado em tempo real a partir das mensagens do grupo. Atualização incremental (EWMA, α=0.2).
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 bg-card">
            <Switch
              checked={!!tomFlag?.enabled}
              disabled={!canManage || flagBusy || !orgId}
              onCheckedChange={toggleTomFlag}
              aria-label="Toggle cortex.tom"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-mono">cortex.tom</span>
              <span className="text-[10px] text-muted-foreground">
                {tomFlag?.enabled ? "on" : "off"}
                {partialRollout && ` · ${tomFlag?.rollout_pct}%`}
              </span>
            </div>
          </div>
        </div>
      </header>

      {flagOff && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Flag cortex.tom está desligada para esta org</AlertTitle>
          <AlertDescription>
            Os perfis exibidos são históricos e não estão sendo atualizados. Para reativar, vá em <strong>Sistema → Feature flags</strong> e habilite <code>cortex.tom</code>.
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          {(availableOrgs?.length ?? 0) > 1 && (
            <Select
              value={orgId ?? ""}
              onValueChange={(v) => {
                setSelectedOrgId(v || null);
                setSelectedGroupId(null);
                setTopicFilter(null);
                setHistoryWindow(null);
              }}
            >
              <SelectTrigger className="w-full md:w-56">
                <SelectValue placeholder="Selecione uma org…" />
              </SelectTrigger>
              <SelectContent>
                {availableOrgs?.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={selectedGroupId ?? ""} onValueChange={(v) => { setSelectedGroupId(v || null); setTopicFilter(null); }}>
            <SelectTrigger className="w-full md:w-72">
              <SelectValue placeholder="Selecione um grupo…" />
            </SelectTrigger>
            <SelectContent>
              {groups?.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name} {g.member_count != null && <span className="text-muted-foreground">· {g.member_count}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou JID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              disabled={!selectedGroupId}
            />
          </div>

          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_at">Mais recentes</SelectItem>
              <SelectItem value="name">Nome (A→Z)</SelectItem>
              <SelectItem value="observation_count">Mais observados</SelectItem>
              <SelectItem value="formality">Formality ↓</SelectItem>
              <SelectItem value="verbosity">Verbosity ↓</SelectItem>
              <SelectItem value="sentiment_baseline">Sentimento ↓</SelectItem>
            </SelectContent>
          </Select>

          <Button
            size="sm"
            variant={filterStale ? "default" : "outline"}
            onClick={() => setFilterStale((v) => !v)}
            disabled={!selectedGroupId}
          >
            <Snowflake className="h-3.5 w-3.5 mr-1" /> Frios (&gt;30d)
          </Button>
          <Button
            size="sm"
            variant={filterNegative ? "default" : "outline"}
            onClick={() => setFilterNegative((v) => !v)}
            disabled={!selectedGroupId}
          >
            <Frown className="h-3.5 w-3.5 mr-1" /> Sentimento –
          </Button>
          <div className="flex items-center gap-1">
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              min={0}
              value={minObs || ""}
              onChange={(e) => setMinObs(Number(e.target.value) || 0)}
              placeholder="≥ obs"
              className="w-20 h-9"
              disabled={!selectedGroupId}
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {topicFilter && (
              <Badge variant="secondary" className="gap-1">
                tópico: {topicFilter}
                <button className="ml-1" onClick={() => setTopicFilter(null)}>×</button>
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={!selectedGroupId}>
                  <History className="h-3.5 w-3.5 mr-1" /> Histórico recente
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuLabel>Janela de leitura</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { setHistoryWindow(24); setHistoryMemberFilter(null); setHistorySearch(""); }}>
                  Últimas 24 horas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setHistoryWindow(48); setHistoryMemberFilter(null); setHistorySearch(""); }}>
                  Últimas 48 horas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setHistoryWindow(72); setHistoryMemberFilter(null); setHistorySearch(""); }}>
                  Últimas 72 horas
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={!filtered.length}>
              <Download className="h-3.5 w-3.5 mr-1" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Membros com perfil" value={String(stats.total)} hint={`${stats.staleCount} frios`} />
          <StatTile label="Formality média" value={stats.avgFormality.toFixed(2)} hint="0 informal · 1 formal" />
          <StatTile label="Verbosity média" value={stats.avgVerbosity.toFixed(2)} hint="0 conciso · 1 verboso" />
          <StatTile label="Sentimento médio" value={stats.avgSentiment.toFixed(2)} hint="-1 neg · +1 pos" />
        </div>
      )}

      {(latestCalibration || contributionRuns?.length) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatTile
            label="Brier recente"
            value={latestCalibration?.brier_score != null ? latestCalibration.brier_score.toFixed(3) : "—"}
            hint={latestCalibration ? `${latestCalibration.sample_size ?? 0} amostras` : "Sem calibração"}
          />
          <StatTile
            label="ECE recente"
            value={latestCalibration?.ece != null ? latestCalibration.ece.toFixed(3) : "—"}
            hint={latestCalibration ? relTime(latestCalibration.created_at) : "Sem calibração"}
          />
          <StatTile
            label="Contribuição ToM"
            value={contributionAvg != null ? contributionAvg.toFixed(3) : "—"}
            hint={contributionRuns?.length ? `${withTomWins}/${contributionRuns.length} vitórias com ToM` : "Sem shadow A/B"}
          />
        </div>
      )}

      {/* Empty / Group picker */}
      {!selectedGroupId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" /> Selecione um grupo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Escolha um grupo para ver o perfil ToM dos participantes.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {groups?.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelectedGroupId(g.id)}
                  className="text-left border border-border rounded-lg p-3 hover:bg-accent transition-colors"
                >
                  <div className="font-medium text-sm truncate">{g.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {g.member_count ?? 0} membros
                  </div>
                </button>
              ))}
              {!groups?.length && (
                <div className="text-sm text-muted-foreground col-span-full">Nenhum grupo ativo encontrado.</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profiles table */}
      {selectedGroupId && (
        <Card>
          <CardContent className="p-0">
            {loadingProfiles ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando perfis…</div>
            ) : !filtered.length ? (
              <div className="p-6 text-sm text-muted-foreground">
                {profiles?.length === 0
                  ? "Nenhum perfil ToM ainda. Flag cortex.tom pode estar off ou o grupo ainda não teve mensagens analisadas."
                  : "Nenhum perfil corresponde aos filtros atuais."}
              </div>
            ) : (
              <>
                {/* Desktop */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Membro</TableHead>
                        <TableHead className="w-20">Obs.</TableHead>
                        <TableHead className="w-32">Formality</TableHead>
                        <TableHead className="w-32">Verbosity</TableHead>
                        <TableHead className="w-32">Sentimento</TableHead>
                        <TableHead>Tópicos</TableHead>
                        <TableHead className="w-28">Último intent</TableHead>
                        <TableHead className="w-32">Atualizado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((p) => {
                        const topics = p.topics_of_interest ?? [];
                        const obsLow = (p.observation_count ?? 0) < 10;
                        return (
                          <TableRow
                            key={p.id}
                            className="cursor-pointer hover:bg-accent/40"
                            onClick={() => setActiveProfile(p)}
                          >
                            <TableCell>
                              <div className="font-medium text-sm">{p.display_name ?? p.member_jid}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[220px]">{p.member_jid}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className="font-mono text-xs">{p.observation_count ?? 0}</span>
                                {obsLow && <Badge variant="outline" className="text-[10px] h-4 px-1">baixa</Badge>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <MiniBar value={Number(p.formality ?? 0)} />
                                <span className="font-mono text-xs">{Number(p.formality ?? 0).toFixed(2)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <MiniBar value={Number(p.verbosity ?? 0)} />
                                <span className="font-mono text-xs">{Number(p.verbosity ?? 0).toFixed(2)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <MiniBar value={Number(p.sentiment_baseline ?? 0)} min={-1} max={1} tone="sentiment" />
                                <span className="font-mono text-xs">{Number(p.sentiment_baseline ?? 0).toFixed(2)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {topics.slice(0, 3).map((t) => (
                                  <Badge
                                    key={t}
                                    variant="secondary"
                                    className="text-[10px] cursor-pointer"
                                    onClick={(e) => { e.stopPropagation(); setTopicFilter(t); }}
                                  >
                                    {t}
                                  </Badge>
                                ))}
                                {topics.length > 3 && (
                                  <Badge variant="outline" className="text-[10px]">+{topics.length - 3}</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {p.last_intent ? <Badge variant="outline" className="text-[10px]">{p.last_intent}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-muted-foreground" title={p.updated_at}>{relTime(p.updated_at)}</span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile */}
                <div className="md:hidden divide-y divide-border">
                  {filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setActiveProfile(p)}
                      className="w-full text-left p-3 hover:bg-accent/40"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm truncate">{p.display_name ?? p.member_jid}</div>
                        <span className="text-[10px] text-muted-foreground">{p.observation_count ?? 0} obs.</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{p.member_jid}</div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <MetricMini label="Form" value={Number(p.formality ?? 0)} />
                        <MetricMini label="Verb" value={Number(p.verbosity ?? 0)} />
                        <MetricMini label="Sent" value={Number(p.sentiment_baseline ?? 0)} sentiment />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(p.topics_of_interest ?? []).slice(0, 4).map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Drawer */}
      <Sheet open={!!activeProfile} onOpenChange={(open) => !open && setActiveProfile(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {activeProfile && (
            <>
              <SheetHeader>
                <SheetTitle>{activeProfile.display_name ?? activeProfile.member_jid}</SheetTitle>
                <SheetDescription className="break-all">{activeProfile.member_jid}</SheetDescription>
                {selectedGroup && (
                  <Badge variant="outline" className="w-fit mt-1">Grupo: {selectedGroup.name}</Badge>
                )}
              </SheetHeader>

              <div className="mt-4 space-y-4">
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs uppercase text-muted-foreground tracking-wide">Confiança do modelo</div>
                    <div className="text-2xl font-bold font-mono mt-1">{activeProfile.observation_count ?? 0}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Quanto maior, mais confiável. Abaixo de 10 observações o modelo ainda é experimental.
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  <Gauge label="Formality" hint="0 informal ↔ 1 formal" value={Number(activeProfile.formality ?? 0)} />
                  <Gauge label="Verbosity" hint="0 conciso ↔ 1 verboso" value={Number(activeProfile.verbosity ?? 0)} />
                  <Gauge label="Sentimento" hint="-1 negativo ↔ +1 positivo" value={Number(activeProfile.sentiment_baseline ?? 0)} min={-1} max={1} tone="sentiment" />
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Tópicos de interesse</div>
                  <div className="flex flex-wrap gap-1">
                    {(activeProfile.topics_of_interest ?? []).map((t) => (
                      <Badge
                        key={t}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() => { setTopicFilter(t); setActiveProfile(null); }}
                      >
                        {t}
                      </Badge>
                    ))}
                    {!activeProfile.topics_of_interest?.length && (
                      <span className="text-xs text-muted-foreground">Sem tópicos observados.</span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Último intent</div>
                  {activeProfile.last_intent ? (
                    <Badge variant="outline">{activeProfile.last_intent}</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Criado</div>
                    <div className="font-mono">{relTime(activeProfile.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Atualizado</div>
                    <div className="font-mono">{relTime(activeProfile.updated_at)}</div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(activeProfile.member_jid);
                      toast.success("JID copiado");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copiar JID
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Recent history drawer */}
      <Sheet open={!!historyWindow} onOpenChange={(o) => { if (!o) setHistoryWindow(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Histórico — últimas {historyWindow}h
            </SheetTitle>
            <SheetDescription>
              {selectedGroup?.name ?? "—"} · leitura somente para análise. Não dispara reprocessamento.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {/* Stats */}
            {historyStats && (
              <div className="grid grid-cols-3 gap-2">
                <StatTile label="Mensagens" value={String(historyStats.total)} hint={historyStats.total >= 2000 ? "limite 2000" : undefined} />
                <StatTile label="Participantes" value={String(historyStats.distinctSenders)} />
                <StatTile
                  label="Mais ativo"
                  value={historyStats.top[0]?.count ? String(historyStats.top[0].count) : "0"}
                  hint={historyStats.top[0]?.name?.slice(0, 20)}
                />
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar conteúdo ou autor…"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
              <Button size="sm" variant="outline" onClick={() => refetchHistory()} disabled={loadingHistory}>
                Atualizar
              </Button>
              <Button size="sm" variant="outline" onClick={exportHistoryCsv} disabled={!filteredMessages.length}>
                <Download className="h-3.5 w-3.5 mr-1" /> CSV
              </Button>
            </div>

            {/* Top senders chips */}
            {historyStats?.top.length ? (
              <div className="flex flex-wrap gap-1">
                {historyStats.top.map((s) => (
                  <Badge
                    key={s.phone}
                    variant={historyMemberFilter === s.phone ? "default" : "secondary"}
                    className="cursor-pointer text-[11px]"
                    onClick={() => setHistoryMemberFilter((cur) => (cur === s.phone ? null : s.phone))}
                  >
                    <User className="h-2.5 w-2.5 mr-1" />
                    {s.name?.slice(0, 18) || s.phone} · {s.count}
                  </Badge>
                ))}
                {historyMemberFilter && (
                  <Badge variant="outline" className="cursor-pointer text-[11px]" onClick={() => setHistoryMemberFilter(null)}>
                    × limpar filtro
                  </Badge>
                )}
              </div>
            ) : null}

            {/* Messages list */}
            <div className="border border-border rounded-lg">
              {loadingHistory ? (
                <div className="p-6 text-sm text-muted-foreground text-center">Carregando histórico…</div>
              ) : !filteredMessages.length ? (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  {recentMessages?.length === 0
                    ? `Nenhuma mensagem nas últimas ${historyWindow}h.`
                    : "Nenhuma mensagem corresponde aos filtros."}
                </div>
              ) : (
                <ScrollArea className="h-[55vh]">
                  <div className="divide-y divide-border">
                    {filteredMessages.map((m) => (
                      <div key={m.id} className="p-3 hover:bg-accent/40 transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <MessageSquare className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="font-medium text-xs truncate">{m.sender_name || m.sender_phone}</span>
                            {m.message_type !== "text" && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1">{m.message_type}</Badge>
                            )}
                          </div>
                          <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                            {format(new Date(m.created_at), "dd/MM HH:mm", { locale: ptBR })}
                          </span>
                        </div>
                        <div className="text-sm text-foreground/90 whitespace-pre-wrap break-words pl-4">
                          {m.content || <span className="text-muted-foreground italic">[sem texto]</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground">
              Janela: {historyWindow}h · {filteredMessages.length} de {recentMessages?.length ?? 0} mensagens · isolamento por grupo via RLS.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
        <div className="text-2xl font-bold font-mono mt-1">{value}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function MetricMini({ label, value, sentiment }: { label: string; value: number; sentiment?: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <MiniBar value={value} min={sentiment ? -1 : 0} max={sentiment ? 1 : 1} tone={sentiment ? "sentiment" : "primary"} />
      <div className="text-[10px] font-mono">{value.toFixed(2)}</div>
    </div>
  );
}

function Gauge({ label, hint, value, min = 0, max = 1, tone = "primary" }: { label: string; hint: string; value: number; min?: number; max?: number; tone?: "primary" | "sentiment" }) {
  const range = max - min;
  const pct = Math.max(0, Math.min(1, (value - min) / range));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium">{label}</span>
        <span className="font-mono">{value.toFixed(2)}</span>
      </div>
      <div className="relative h-2 rounded-full bg-muted overflow-hidden">
        {tone === "sentiment" && <div className="absolute top-0 bottom-0 w-px bg-foreground/30" style={{ left: "50%" }} />}
        {tone === "sentiment" ? (
          <div
            className={`absolute top-0 bottom-0 ${pct < 0.5 ? "bg-destructive" : "bg-success"}`}
            style={{
              left: pct < 0.5 ? `${pct * 100}%` : "50%",
              width: `${Math.abs(pct - 0.5) * 200}%`,
            }}
          />
        ) : (
          <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${pct * 100}%` }} />
        )}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}

export default function CortexTomExplorer() {
  return (
    <RoleGate permission="cortex.tom.read" fallback={<NotFound />}>
      <CortexTomExplorerInner />
    </RoleGate>
  );
}
